import {
    ComponentInformation,
    ComponentInformationResponse,
    ProductSizeInfoGroup,
    ProductSizeMeasurement,
} from './ProductSizeInfo.types';

const isGroupedSizeData = (value: unknown): value is ProductSizeInfoGroup[] => {
    if (!Array.isArray(value) || value.length === 0) {
        return false;
    }

    const firstItem = value[0] as ProductSizeInfoGroup;
    return Array.isArray(firstItem?.measurements) && (firstItem.sizeId != null || !!firstItem.sizeName);
};

const normalizeMeasurement = (measurement: Record<string, unknown>): ProductSizeMeasurement => ({
    styleMeasurementPOMId: Number(measurement.styleMeasurementPOMId ?? measurement.StyleMeasurementPOMId ?? 0),
    styleId: Number(measurement.styleId ?? measurement.StyleId ?? 0),
    sequence: Number(measurement.sequence ?? measurement.Sequence ?? 0),
    priority: Number(measurement.priority ?? measurement.Priority ?? 0),
    pomRef: Number(measurement.pomRef ?? measurement.POMRef ?? 0),
    pomCode: String(measurement.pomCode ?? measurement.POMCode ?? ''),
    pomNameTr: String(measurement.pomNameTr ?? measurement.POMNameTr ?? ''),
    pomNameEn: String(measurement.pomNameEn ?? measurement.POMNameEn ?? ''),
    description: String(measurement.description ?? measurement.Description ?? ''),
    gradeMeasMetric: Number(measurement.gradeMeasMetric ?? measurement.GradeMeasMetric ?? 0),
    toleranceMinusMetric: Number(measurement.toleranceMinusMetric ?? measurement.ToleranceMinusMetric ?? 0),
    tolerancePlusMetric: Number(measurement.tolerancePlusMetric ?? measurement.TolerancePlusMetric ?? 0),
    pomPartId:
        measurement.pomPartId != null || measurement.POMPartId != null
            ? Number(measurement.pomPartId ?? measurement.POMPartId)
            : null,
    garmentUserName: String(measurement.garmentUserName ?? measurement.GarmentUserName ?? ''),
});

const groupFlatMeasurements = (measurements: Record<string, unknown>[]): ProductSizeInfoGroup[] => {
    const grouped = new Map<string, ProductSizeInfoGroup>();

    measurements.forEach((measurement) => {
        const sizeId = Number(measurement.sizeId ?? measurement.SizeId ?? measurement.sizeRef ?? 0);
        const sizeName = String(
            measurement.sizeName ?? measurement.SizeName ?? measurement.size ?? measurement.Size ?? sizeId
        );
        const groupKey = `${sizeId}-${sizeName}`;

        if (!grouped.has(groupKey)) {
            grouped.set(groupKey, {
                sizeId,
                sizeName,
                measurements: [],
            });
        }

        grouped.get(groupKey)?.measurements.push(normalizeMeasurement(measurement));
    });

    return Array.from(grouped.values());
};

const isComponentInformationResponse = (value: unknown): value is ComponentInformationResponse => {
    if (!value || typeof value !== 'object') {
        return false;
    }

    return Array.isArray((value as ComponentInformationResponse).componentInformationList);
};

const unwrapNestedData = (value: unknown, maxDepth = 5): unknown => {
    let current = value;

    for (let depth = 0; depth < maxDepth; depth += 1) {
        if (isComponentInformationResponse(current) || isGroupedSizeData(current) || Array.isArray(current)) {
            return current;
        }

        if (!current || typeof current !== 'object' || !('data' in current)) {
            break;
        }

        const nestedData = (current as Record<string, unknown>).data;

        if (nestedData == null) {
            break;
        }

        current = nestedData;
    }

    return current;
};

const getPlmCodeFromComponent = (component: ComponentInformation): string | null => {
    if (!component.plmCode) {
        return null;
    }

    return String(component.plmCode);
};

export const extractPlmCode = (responseData: unknown): string | null => {
    const plmCodes = extractPlmCodes(responseData);
    return plmCodes.length > 0 ? plmCodes[0] : null;
};

export const extractPlmCodes = (responseData: unknown): string[] => {
    const payload = unwrapNestedData(responseData);

    if (!isComponentInformationResponse(payload)) {
        return [];
    }

    return payload.componentInformationList
        .map(getPlmCodeFromComponent)
        .filter((plmCode): plmCode is string => plmCode != null && plmCode !== '');
};

export const extractComponentInformationList = (responseData: unknown): ComponentInformation[] => {
    const payload = unwrapNestedData(responseData);

    if (!isComponentInformationResponse(payload)) {
        return [];
    }

    return payload.componentInformationList;
};

export const mapStyleMeasurementResponse = (responseData: unknown): ProductSizeInfoGroup[] => {
    if (!responseData) {
        return [];
    }

    const payload = unwrapNestedData(responseData);
    let measurementPayload: unknown = payload;

    if (payload && typeof payload === 'object' && !Array.isArray(payload) && !isGroupedSizeData(payload)) {
        const objectPayload = payload as Record<string, unknown>;
        measurementPayload =
            objectPayload.sizeMeasurements ??
            objectPayload.styleMeasurements ??
            objectPayload.measurements ??
            payload;
    }

    if (isGroupedSizeData(measurementPayload)) {
        return measurementPayload.map((sizeGroup) => ({
            sizeId: sizeGroup.sizeId,
            sizeName: sizeGroup.sizeName,
            measurements: sizeGroup.measurements.map((measurement) =>
                normalizeMeasurement(measurement as unknown as Record<string, unknown>)
            ),
        }));
    }

    if (Array.isArray(measurementPayload)) {
        return groupFlatMeasurements(measurementPayload as Record<string, unknown>[]);
    }

    return [];
};
